import { getDirectRelationship } from '@cognite/apm-client';
import type {
  Action,
  ActionUpsertType,
  APMAppDataService,
  InFieldUser,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { CogniteClientDependencies } from '@cognite/fdm-client';

import type { IActionsService } from './actions-service-interface';

export type APMClientDependencies = {
  actions: Pick<APMAppDataService<Action>, 'upsert' | 'delete'>;
  userInstanceSpace: string;
};

export class ActionsService extends FDMClient implements IActionsService {
  apmClient: APMClientDependencies;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.apmClient = apmClient;
  }

  async createActions(actions: Action[], user: InFieldUser) {
    const upsertedActions: Action[] = actions.map((action) => ({
      ...action,
      createdBy: user,
    }));
    return this.upsertActions(upsertedActions).then(
      (result) => result.data.items[0]
    );
  }

  async updateAction(action: Action, user: InFieldUser) {
    const upsertedAction: Action = {
      ...action,
      updatedBy: user,
    };
    return this.upsertActions([upsertedAction]);
  }

  async upsertActions(actions: Action[]) {
    const upsertedActions: ActionUpsertType[] = actions.map((action) => {
      const conditionalActionRelationship = getDirectRelationship(
        action.conditionalActions
      );
      const targetRelationship = getDirectRelationship(action.target);
      const createdByRelationship = getDirectRelationship({
        externalId: action.createdBy?.externalId,
        space: this.apmClient.userInstanceSpace,
      });
      const updatedByRelationship = getDirectRelationship({
        externalId: action.updatedBy?.externalId,
        space: this.apmClient.userInstanceSpace,
      });

      return {
        ...action,
        conditionalActions: conditionalActionRelationship,
        target: targetRelationship,
        createdBy: createdByRelationship,
        updatedBy: updatedByRelationship,
      };
    });
    return this.apmClient.actions.upsert(upsertedActions);
  }

  async deleteActions(externalIds: string[]) {
    return this.apmClient.actions.delete(externalIds);
  }
}

export const createActionsService = (
  ...args: ConstructorParameters<typeof ActionsService>
): IActionsService => new ActionsService(...args);
